/**
 * UX Sprint 2 — Campaign Strategy composite overview.
 *
 * Single-page summary of the 4 strategy pillars, each a card that
 * routes the user into the existing panel. Reuses the existing
 * tabbing — when a sub-tab is clicked the user lands on the same
 * legacy panel they have today.
 *
 * Zero new backend calls. Status read from `project.stages` and from
 * existing `project.*` fields the panels already use.
 */
import StageSummaryCard from "./StageSummaryCard";

function mapStatus(raw) {
    const s = (raw || "").toLowerCase();
    if (s === "approved") return "approved";
    if (["processing", "running", "in_progress", "leased"].includes(s)) return "in_progress";
    if (["pending", "completed", "manually_completed", "changes_requested", "rejected"].includes(s)) return "needs_review";
    if (s === "failed") return "failed";
    return "not_started";
}

export default function CampaignStrategyWorkspace({ project, onOpenTab }) {
    const bpStatus = mapStatus(project?.stages?.business_plan?.status);
    const socialHasData = !!(project?.social_strategy && Object.keys(project.social_strategy).length > 0);
    const distHasData = !!(project?.distribution_opportunity && Object.keys(project.distribution_opportunity).length > 0);
    const influencersHasData = !!(project?.influencers && (project.influencers.length || Object.keys(project.influencers).length));

    return (
        <div className="px-6 lg:px-10 py-8" data-testid="campaign-strategy-workspace">
            <div className="mb-5">
                <div className="label-micro mb-1 text-zinc-500">Campaign Strategy · Overview</div>
                <p className="text-sm text-zinc-400 max-w-2xl">
                    Lock the four pillars of the campaign. Open any card to review or approve.
                </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <StageSummaryCard
                    title="Business Plan"
                    subtitle="Growth logic, KPIs, and budget breakdown."
                    status={bpStatus}
                    onOpen={() => onOpenTab && onOpenTab("business_plan")}
                    openLabel="Open Business Plan"
                    testid="campaign-card-business-plan"
                >
                    {bpStatus === "approved" ? "Business plan is approved." :
                     bpStatus === "in_progress" ? "Agent is generating the plan…" :
                     bpStatus === "needs_review" ? "Awaiting your approval." :
                     bpStatus === "failed" ? "Last run failed. Retry from the panel." :
                     "Not started yet."}
                </StageSummaryCard>

                <StageSummaryCard
                    title="Social Content Plan"
                    subtitle="What to post, by platform and cadence."
                    status={socialHasData ? "needs_review" : "not_started"}
                    onOpen={() => onOpenTab && onOpenTab("social_strategy")}
                    openLabel="Open Social Content Plan"
                    testid="campaign-card-social"
                >
                    {socialHasData ? "Social plan data is ready to review." : "Run the social strategy agent to populate."}
                </StageSummaryCard>

                <StageSummaryCard
                    title="Distribution Channels"
                    subtitle="How to push the campaign — owned, earned, paid."
                    status={distHasData ? "needs_review" : "not_started"}
                    onOpen={() => onOpenTab && onOpenTab("distribution")}
                    openLabel="Open Distribution Channels"
                    testid="campaign-card-distribution"
                >
                    {distHasData ? "Distribution opportunities are ready to review." : "Run the distribution agent to populate."}
                </StageSummaryCard>

                <StageSummaryCard
                    title="Influencer Ideas"
                    subtitle="Voices and creators who can amplify the campaign."
                    status={influencersHasData ? "needs_review" : "not_started"}
                    onOpen={() => onOpenTab && onOpenTab("influencers")}
                    openLabel="Open Influencer Ideas"
                    testid="campaign-card-influencers"
                >
                    {influencersHasData ? "Influencer ideas available." : "Run the influencer agent to populate."}
                </StageSummaryCard>
            </div>
        </div>
    );
}
